'use client';

import type { ExternalToast } from 'sonner';

import { toast as sonnerToast } from 'sonner';

import { ärRedanVisat, markeraSomVisat } from 'src/lib/fel/visaFel';

const nyligenVisade = new Map<string, { tidpunkt: number; id: string | number }>();
const DÄMPNING_MS = 2000;

function rensaGamla() {
  const nu = Date.now();
  for (const [nyckel, post] of nyligenVisade.entries()) {
    if (nu - post.tidpunkt > 10000) {
      nyligenVisade.delete(nyckel);
    }
  }
}

function genereraNyckel(
  meddelande: any,
  data?: ExternalToast
): { nyckel: string; textMeddelande: any } {
  // Om meddelandet är ett felobjekt
  if (meddelande && typeof meddelande === 'object' && !(meddelande as any).$$typeof) {
    if (ärRedanVisat(meddelande)) {
      return { nyckel: '__REDAN_VISAT__', textMeddelande: '' };
    }
    markeraSomVisat(meddelande);
    const text =
      (meddelande as any)?.response?.data?.message ||
      (meddelande as any)?.message ||
      'Ett fel uppstod';
    const referens =
      (meddelande as any)?.response?.data?.felReferens ||
      (meddelande as any)?.response?.headers?.['x-fel-referens'];
    const nyckel = (data?.id ? String(data.id) : (referens ?? text)).trim();
    return { nyckel, textMeddelande: text };
  }

  if (typeof meddelande === 'string') {
    const felRefMatch = meddelande.match(/FEL-[A-Z0-9]+/);
    const referens = felRefMatch ? felRefMatch[0] : undefined;
    const nyckel = (data?.id ? String(data.id) : (referens ?? meddelande)).trim();
    return { nyckel, textMeddelande: meddelande };
  }

  return {
    nyckel: data?.id ? String(data.id) : String(meddelande),
    textMeddelande: meddelande,
  };
}

function anropaMedDeduplicering(
  metod: 'message' | 'error' | 'success' | 'info' | 'warning' | 'loading' | 'default',
  meddelande: any,
  data?: ExternalToast
): string | number {
  rensaGamla();

  const { nyckel, textMeddelande } = genereraNyckel(meddelande, data);
  if (nyckel === '__REDAN_VISAT__') {
    return data?.id ?? '';
  }

  const nu = Date.now();
  const tidigare = nyligenVisade.get(nyckel);

  if (tidigare && nu - tidigare.tidpunkt < DÄMPNING_MS) {
    // Om detta anrop har explicit action men tidigare inte hade det, uppdatera
    if (data?.action) {
      return (sonnerToast as any)[metod === 'default' ? 'message' : metod](textMeddelande, {
        ...data,
        id: tidigare.id,
      });
    }
    return tidigare.id;
  }

  const tilldelatId = data?.id ?? nyckel;
  const options: ExternalToast = {
    ...data,
    id: tilldelatId,
  };

  let id: string | number;
  if (metod === 'default') {
    id = sonnerToast(textMeddelande, options);
  } else {
    id = (sonnerToast as any)[metod](textMeddelande, options);
  }

  nyligenVisade.set(nyckel, { tidpunkt: nu, id: id ?? tilldelatId });
  return id ?? tilldelatId;
}

export const toast = Object.assign(
  (message: any, data?: ExternalToast) => anropaMedDeduplicering('default', message, data),
  {
    error: (message: any, data?: ExternalToast) => anropaMedDeduplicering('error', message, data),
    success: (message: any, data?: ExternalToast) => anropaMedDeduplicering('success', message, data),
    info: (message: any, data?: ExternalToast) => anropaMedDeduplicering('info', message, data),
    warning: (message: any, data?: ExternalToast) => anropaMedDeduplicering('warning', message, data),
    loading: (message: any, data?: ExternalToast) => anropaMedDeduplicering('loading', message, data),
    message: (message: any, data?: ExternalToast) => anropaMedDeduplicering('message', message, data),
    promise: sonnerToast.promise.bind(sonnerToast),
    custom: sonnerToast.custom.bind(sonnerToast),
    dismiss: sonnerToast.dismiss.bind(sonnerToast),
    getHistory: sonnerToast.getHistory?.bind(sonnerToast),
  }
);
